import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, apiCall } from '../AuthContext';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, TrendingUp, CreditCard, Activity, Check, ArrowRight, Loader2, Package, ListOrdered, Printer, Edit3, Trash2, Gift, Copy, Share2 } from 'lucide-react';
import { SquareLogo, ShopifyLogo, EbayLogo } from '../components/BrandLogos';

const COLORS = ['#0052FF', '#10B981', '#FACC15', '#EF4444'];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [removingEvent, setRemovingEvent] = useState(null);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await apiCall('/api/dashboard', { method: 'GET' });
      setData(res.data);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    apiCall('/api/referrals/me', { method: 'GET' })
      .then(r => setReferral(r.data))
      .catch(() => {});
  }, []);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && data) {
      let attempts = 0;
      const poll = async () => {
        if (attempts >= 5) return;
        attempts++;
        try {
          const res = await apiCall(`/api/checkout/status/${sessionId}`, { method: 'GET' });
          if (res.data.payment_status === 'paid') {
            fetchDashboard();
            window.history.replaceState({}, '', '/dashboard');
            return;
          }
        } catch {}
        setTimeout(poll, 2000);
      };
      poll();
    }
  }, [searchParams, data, fetchDashboard]);

  const handleUpgrade = async (plan) => {
    if (plan.subscription_price <= 0) return;
    setUpgrading(plan.subscription_id);
    try {
      // Shopify App Store-installed merchants MUST use Shopify Billing API,
      // never Stripe (App Store policy 1.2.1). Route them to the Shopify
      // plan picker which calls appSubscriptionCreate under the hood.
      if (data?.user?.billing_provider === 'shopify') {
        window.location.href = `/shopify-plan-select?plan=${plan.subscription_id}`;
        return;
      }
      const res = await apiCall('/api/checkout', {
        method: 'POST',
        data: { subscription_id: plan.subscription_id, origin_url: window.location.origin }
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Error upgrading plan');
    } finally {
      setUpgrading(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
    </div>
  );

  if (!data) return <p className="text-slate-500">Failed to load dashboard data.</p>;

  // ── ADMIN DASHBOARD ──
  if (data.is_admin) {
    const planCounts = data.plans?.map(plan => {
      const usersInPlan = data.subscription_users?.filter(u => u.subscription_id === plan.subscription_id) || [];
      return { name: plan.subscription_name, users: usersInPlan.length, revenue: usersInPlan.reduce((s, u) => s + Number(u.amount_paid || 0), 0) };
    }) || [];

    return (
      <div className="space-y-6" data-testid="admin-dashboard">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Admin Dashboard</h2>
          <p className="text-sm text-slate-500 mt-1">Overview of your platform metrics</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: data.total_users, icon: Users, color: '#0052FF' },
            { label: 'Active Subs', value: data.active_subscriptions, icon: Activity, color: '#10B981' },
            { label: 'Expired Subs', value: data.expired_subscriptions, icon: CreditCard, color: '#EF4444' },
            { label: 'Revenue', value: `$${data.total_revenue?.toFixed(2)}`, icon: TrendingUp, color: '#FACC15' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid={`stat-card-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{s.label}</span>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '15' }}>
                    <Icon className="w-4 h-4" style={{ color: s.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-[#0F172A]">{s.value}</p>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="users-chart">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Users by Plan</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={planCounts} dataKey="users" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {planCounts.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="revenue-chart">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Revenue by Plan</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={planCounts}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="revenue" fill="#0052FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }

  // ── USER DASHBOARD ──
  const sub = data.subscription;
  const currentPlan = data.plans?.find(p => p.subscription_id === sub?.subscription_id);
  const daysLeft = sub?.expires_at ? Math.max(0, Math.ceil((new Date(sub.expires_at) - new Date()) / 86400000)) : 0;
  const skuUsed = sub ? (sub.allowed_sku - sub.remaining_sku) : 0;
  const skuPercent = sub && sub.allowed_sku > 0 ? Math.round((skuUsed / sub.allowed_sku) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="user-dashboard">
      <div>
        <h2 className="text-2xl font-bold text-[#0F172A]">Welcome, {user?.username || user?.first_name}</h2>
        <p className="text-sm text-slate-500 mt-1">Manage your subscription and account</p>
      </div>

      {/* Activity Overview */}
      <div className="grid grid-cols-1 gap-4">
        {[
          { label: 'Total Products', value: data.total_products ?? 0, icon: Package, color: '#0052FF', testid: 'stat-total-products', to: '/product-list', hint: 'View all products' },
        ].map((s, i) => {
          const Icon = s.icon;
          const cardInner = (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{s.label}</span>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '15' }}>
                  <Icon className="w-4 h-4" style={{ color: s.color }} />
                </div>
              </div>
              <p className="text-3xl font-bold text-[#0F172A]">{Number(s.value).toLocaleString()}</p>
              <p className="mt-2 text-xs font-medium text-slate-400 group-hover:text-[#0052FF] inline-flex items-center gap-1 transition-colors">
                {s.hint} <ArrowRight className="w-3 h-3" />
              </p>
            </>
          );
          const cardClass = "group bg-white border border-slate-200 rounded-xl p-6 shadow-sm cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-[#0052FF]/30";
          if (s.to) {
            return (
              <Link key={i} to={s.to} className={cardClass} data-testid={s.testid}>{cardInner}</Link>
            );
          }
          return (
            <button
              key={i}
              type="button"
              onClick={() => {
                const el = document.querySelector(`[data-testid="${s.anchor}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`${cardClass} text-left w-full`}
              data-testid={s.testid}
            >
              {cardInner}
            </button>
          );
        })}
      </div>

      {/* Recent Products */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="recent-products-card">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-[#0F172A]">Recent Products</h3>
          <p className="text-xs text-slate-500">Your last 5 product pushes across Shopify, Square, and eBay</p>
        </div>
        {data.recent_products && data.recent_products.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-products-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold w-20">Image</th>
                  <th className="text-left px-4 py-3 font-semibold">Item</th>
                  <th className="text-left px-4 py-3 font-semibold">Platform</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_products.slice(0, 5).map((p, idx) => {
                  const platform = (p.platform || '').toLowerCase();
                  const PlatformLogo = platform === 'shopify' ? ShopifyLogo : platform === 'square' ? SquareLogo : platform === 'ebay' ? EbayLogo : null;
                  const platformLabel = platform === 'shopify' ? 'Shopify' : platform === 'square' ? 'Square' : platform === 'ebay' ? 'eBay' : p.platform;
                  const quickEdit = () => {
                    const sp = String(p.selling_price || '').replace(/[^0-9.]/g, '');
                    const prefill = {
                      product_name: p.product_name || '',
                      barcode: p.barcode || '',
                      retail_price: p.retail_price || '',
                      selling_price: sp ? Number(sp) : 0,
                      image_url: p.image_url || '',
                    };
                    navigate('/product-form', { state: { prefillProduct: prefill } });
                  };
                  const removeRow = async () => {
                    if (!p.id) { return; }
                    if (!window.confirm('Remove this from Recent Products?')) return;
                    setRemovingEvent(p.id);
                    try {
                      await apiCall(`/api/push-events/${p.id}`, { method: 'DELETE' });
                      setData(prev => ({
                        ...prev,
                        recent_products: (prev.recent_products || []).filter(r => r.id !== p.id),
                        total_listings: Math.max(0, (prev.total_listings || 1) - 1),
                      }));
                    } catch { alert('Failed to remove.'); } finally { setRemovingEvent(null); }
                  };
                  return (
                    <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors" data-testid={`recent-product-row-${idx}`}>
                      <td className="px-6 py-3">
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-contain border border-slate-200 bg-white" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400">
                            <Package className="w-5 h-5" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-[#0F172A] max-w-xs truncate">{p.product_name}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {PlatformLogo && <PlatformLogo className="w-5 h-5" />}
                          <span className="text-slate-700">{platformLabel}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">{p.status || 'Listed'}</span>
                          <button
                            onClick={quickEdit}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#0052FF]/10 text-[#0052FF] hover:bg-[#0052FF] hover:text-white text-xs font-semibold transition-colors"
                            title="Quick edit / reprint"
                            data-testid={`quick-edit-recent-${idx}`}
                          >
                            <Edit3 className="w-3.5 h-3.5" /> Quick Edit
                          </button>
                          <button
                            onClick={removeRow}
                            disabled={removingEvent === p.id || !p.id}
                            className="inline-flex items-center justify-center w-8 h-7 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Remove from Recent Products"
                            data-testid={`remove-recent-${idx}`}
                          >
                            {removingEvent === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-10 text-center text-sm text-slate-500" data-testid="recent-products-empty">
            No products pushed yet. Scan a barcode in the Product Form and push to any platform to see activity here.
          </div>
        )}
      </div>

      {/* Refer & Earn Card — visible to all users (admin + customers) */}
      {referral && (
        <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0052FF] rounded-2xl p-6 text-white" data-testid="referral-card">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
                <Gift className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Give 20% off · Get 1 free month</h3>
                <p className="text-xs text-slate-300">Your friend saves 20% on their first month — you get a full free month when they pay. Unlock bigger rewards at 5, 10, and 25 referrals.</p>
              </div>
            </div>
            <button onClick={() => navigate('/rewards')} className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-2 rounded-lg" data-testid="go-to-rewards-btn">
              View full rewards →
            </button>
          </div>

          <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg border border-white/20 px-3 py-2 mb-4">
            <input
              readOnly
              value={referral.link || ''}
              onClick={e => e.target.select()}
              className="flex-1 text-xs bg-transparent outline-none font-mono text-white placeholder-white/50"
              data-testid="referral-link-input"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(referral.link || '').then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                });
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white text-[#0052FF] text-xs font-semibold hover:bg-slate-100"
              data-testid="referral-copy-btn"
            >
              <Copy className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <a href={`https://wa.me/?text=${encodeURIComponent(`I've been using XLabelSync for barcode scanning + auto-labels + Shopify/Square sync. Sign up with my link and save 20% on your first month: ${referral.link || ''}`)}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#25D366] text-white text-xs font-semibold hover:opacity-90" data-testid="referral-share-whatsapp">WhatsApp</a>
            <a href={`mailto:?subject=${encodeURIComponent('Try XLabelSync — 20% off your first month')}&body=${encodeURIComponent(`Hey! I use XLabelSync for my inventory + label printing. Sign up with my link and save 20% on your first month:\n\n${referral.link || ''}`)}`}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/15 backdrop-blur border border-white/20 text-white text-xs font-semibold hover:bg-white/25" data-testid="referral-share-email">Email</a>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Barcode scan → auto price tag → push to Shopify/Square. XLabelSync saves me hours. Get 20% off your first month: ${referral.link || ''}`)}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-black text-white text-xs font-semibold hover:opacity-90" data-testid="referral-share-x">X / Twitter</a>
            <a href={`sms:&body=${encodeURIComponent(`Try XLabelSync, save 20% on your first month: ${referral.link || ''}`)}`}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#0052FF] text-white text-xs font-semibold hover:opacity-90 border border-white/20" data-testid="referral-share-sms">SMS</a>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/10">
            <div><p className="text-[10px] uppercase tracking-wider text-slate-400">Clicks</p><p className="text-lg font-bold text-white" data-testid="referral-clicks">{referral.clicks || 0}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-slate-400">Sign-ups</p><p className="text-lg font-bold text-white" data-testid="referral-signups">{referral.signups || 0}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-slate-400">Paid referrals</p><p className="text-lg font-bold text-green-300" data-testid="referral-paid">{referral.paid_referrals || 0}</p></div>
          </div>
        </div>
      )}

      {/* Current Plan */}
      {sub && currentPlan ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="current-plan-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-1">Current Plan</p>
              <h3 className="text-xl font-bold text-[#0F172A]">{currentPlan.subscription_name}</h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {sub.status}
            </span>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <p className="text-xs text-slate-400 mb-1">SKU Usage</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0052FF] rounded-full transition-all" style={{ width: `${skuPercent}%` }} />
                </div>
                <span className="text-sm font-medium text-[#0F172A]">{skuPercent}%</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{skuUsed} / {sub.allowed_sku >= 99999 ? '∞' : sub.allowed_sku} SKUs used</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Days Remaining</p>
              <p className="text-2xl font-bold text-[#0F172A]">{daysLeft}</p>
              <p className="text-xs text-slate-500">Expires {new Date(sub.expires_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Amount Paid</p>
              <p className="text-2xl font-bold text-[#0F172A]">${sub.amount_paid || 0}</p>
            </div>
          </div>
          {sub.subscription_id !== '1000' && sub.status === 'active' && (
            <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">You'll keep access until the end of the current billing period.</p>
              <button
                onClick={async () => {
                  if (!window.confirm('Cancel your subscription? You will retain access until the current period ends.')) return;
                  try {
                    await apiCall('/api/cancel-subscription', { method: 'POST' });
                    const res = await apiCall('/api/dashboard', { method: 'GET' });
                    setData(res.data);
                    alert('Your subscription has been cancelled.');
                  } catch (e) { alert(e.response?.data?.detail || 'Failed to cancel'); }
                }}
                className="text-sm font-semibold px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                data-testid="cancel-subscription-btn"
              >
                Cancel Subscription
              </button>
            </div>
          )}
          {sub.status === 'cancelled' && (
            <div className="mt-5 pt-5 border-t border-slate-100">
              <p className="text-xs text-red-600 font-medium">Subscription cancelled. Access continues until {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : 'the end of this billing cycle'}. Choose a plan below to reactivate.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6" data-testid="no-subscription-alert">
          <p className="text-yellow-800 font-medium">No active subscription found. Choose a plan below to get started.</p>
        </div>
      )}

      {/* Upgrade Plans */}
      <div>
        <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Available Plans</h3>
        {data?.user?.billing_provider === 'shopify' && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm" data-testid="shopify-billing-banner">
            <strong>Billed through Shopify.</strong> Because you installed XLabelSync from the Shopify App Store, all charges are processed via Shopify's Billing API and appear on your regular Shopify invoice. Upgrades route through Shopify — Stripe is never used for App Store installs.
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.plans?.filter(p => p.subscription_id !== '1000').sort((a,b)=>(a.sort_order||0)-(b.sort_order||0)).map(plan => {
            const isCurrent = plan.subscription_id === sub?.subscription_id;
            const currentPrice = currentPlan?.subscription_price || 0;
            const isDowngrade = plan.subscription_price < currentPrice;
            const isPopular = !!plan.is_popular;
            const isContact = !!plan.is_contact;
            const isShopify = data?.user?.billing_provider === 'shopify';
            return (
              <div key={plan.subscription_id}
                className={`relative bg-white rounded-xl p-5 border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${isCurrent ? 'border-[#0052FF] ring-1 ring-[#0052FF]/20' : isPopular ? 'border-[#0052FF] border-2' : 'border-slate-200'}`}
                data-testid={`plan-card-${plan.subscription_id}`}
              >
                {isPopular && !isCurrent && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-[#0052FF] text-white text-[10px] font-bold rounded-full">⭐ POPULAR</div>
                )}
                <h4 className="font-bold text-[#0F172A] text-base">{plan.subscription_name}</h4>
                <p className="text-xs text-slate-500 min-h-[32px] mt-0.5">{plan.tagline || ''}</p>
                <p className="text-2xl font-black text-[#0F172A] mt-2">
                  {isContact ? 'Custom' : <>${plan.subscription_price}<span className="text-xs font-normal text-slate-500">/mo</span></>}
                </p>
                <ul className="mt-3 space-y-1.5 min-h-[140px]">
                  {(plan.features || []).slice(0, 5).map((f, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 text-xs text-slate-600">
                      <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => isContact ? window.location.href = '/contact' : handleUpgrade(plan)}
                  disabled={isCurrent || upgrading === plan.subscription_id}
                  data-testid={`upgrade-btn-${plan.subscription_id}`}
                  className={`mt-4 w-full py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${isCurrent ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : isContact ? 'bg-slate-900 hover:bg-black text-white' : isPopular ? 'bg-[#0052FF] hover:bg-[#0040CC] text-white' : isDowngrade ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-[#0052FF] hover:bg-[#0040CC] text-white'}`}
                >
                  {upgrading === plan.subscription_id ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? 'Current Plan' : isContact ? 'Contact Us' : isShopify ? <>Subscribe via Shopify <ArrowRight className="w-4 h-4" /></> : isDowngrade ? 'Downgrade' : <>Upgrade <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
